import { ColumnFiltersState, TableState } from "@tanstack/react-table";
import {
    encodeGlobalFilter,
    encodeSorting,
} from "tanstack-table-search-params/encoder-decoder";
import z from "zod";

import { assertNever } from "../../utils/obj";

import { encodeValuesWithCodecMap } from "./codecs/columnFiltersCodec";
import { encodeColumnOrder } from "./codecs/columnOrderCodec";
import { encodeColumnPinning } from "./codecs/columnPinningCodec";
import { encodeColumnVisibility } from "./codecs/columnVisibilityCodec";
import {
    coerceDataParam,
    encodeDataParam,
    DataParamsCodecMap,
} from "./dataParams";
import {
    columnBaselineFingerprint,
    COLUMN_ORDER_PARAM_NAME,
    COLUMN_PINNING_PARAM_NAME,
    COLUMN_VISIBILITY_PARAM_NAME,
    withBaselineFingerprint,
} from "./positionalColumnParams";
import { PresetSearchParamsCustomDefaultValues } from "./types";

type CodecMap = Record<string, z.ZodCodec>;
/**
 * Formats table state into URL search parameters string
 * @param state - The table state containing filters, visibility, sorting, etc.
 * @param url - Optional existing URL to preserve existing search parameters
 * @returns URL-encoded search parameters string
 */
export function formatTableStateSearchUrl(
    url: string,
    state: Partial<TableState>,
    defaultValues: PresetSearchParamsCustomDefaultValues,
    codecMap: CodecMap,
    onError?: (error: unknown) => void,
    dataParamsCodecMap: DataParamsCodecMap = {},
    storedDataParams: Record<string, unknown> = {}
) {
    const newURL = new URL(url, window.location.origin);
    const existingParams = Object.fromEntries(newURL.searchParams.entries());

    const encoders = [
        {
            key: "columnFilters" as const,
            encoder: (value: ColumnFiltersState) =>
                encodeValuesWithCodecMap(value, codecMap, onError),
            value: state.columnFilters,
        },
        {
            key: "columnVisibility" as const,
            encoder: encodeColumnVisibility,
            value: state.columnVisibility,
        },
        {
            key: "globalFilter" as const,
            encoder: encodeGlobalFilter,
            value: state.globalFilter,
        },
        {
            key: "sorting" as const,
            encoder: encodeSorting,
            value: state.sorting,
        },
        {
            key: "columnOrder" as const,
            encoder: encodeColumnOrder,
            value: state.columnOrder,
        },
        {
            key: "columnPinning" as const,
            encoder: encodeColumnPinning,
            value: state.columnPinning,
        },
    ];

    const searchParams = encoders.reduce<Record<string, string>>(
        (acc, { key, encoder, value }) => {
            switch (key) {
                case "columnFilters": {
                    if (
                        value === undefined ||
                        (Array.isArray(value) && value.length === 0)
                    ) {
                        return acc;
                    }
                    const encodedValue = encoder(value);
                    // Empty when every filter failed to encode
                    if (
                        typeof encodedValue !== "string" ||
                        encodedValue === ""
                    ) {
                        return acc;
                    }
                    return {
                        ...acc,
                        [key]: encodedValue,
                    };
                }
                case "columnVisibility": {
                    if (value === undefined || !defaultValues.columnOrder) {
                        return acc;
                    }
                    const encodedValue = encoder(
                        { ...defaultValues.columnVisibility, ...value }, // so the absent keys are filled with default values
                        defaultValues.columnOrder,
                        defaultValues.columnVisibility
                    );
                    if (encodedValue === null) {
                        return acc;
                    }
                    return {
                        ...acc,
                        [COLUMN_VISIBILITY_PARAM_NAME]: encodedValue,
                    };
                }
                case "globalFilter": {
                    if (value === undefined || value === "") {
                        return acc;
                    }
                    const encodedValue = encoder(value);
                    if (typeof encodedValue !== "string") {
                        return acc;
                    }
                    return {
                        ...acc,
                        [key]: encodedValue,
                    };
                }
                case "sorting": {
                    if (value === undefined) {
                        return acc;
                    }
                    const encodedValue = encoder(value, {
                        defaultValue: defaultValues.sorting,
                    });
                    if (typeof encodedValue !== "string") {
                        return acc;
                    }
                    return {
                        ...acc,
                        [key]: encodedValue,
                    };
                }
                case "columnOrder": {
                    if (value === undefined || !defaultValues.columnOrder) {
                        return acc;
                    }
                    const encodedValue = encoder(
                        value,
                        defaultValues.columnOrder
                    );
                    if (encodedValue === null) {
                        return acc;
                    }
                    return {
                        ...acc,
                        [COLUMN_ORDER_PARAM_NAME]: encodedValue,
                    };
                }
                case "columnPinning": {
                    if (value === undefined || !defaultValues.columnOrder) {
                        return acc;
                    }
                    const encodedValue = encoder(
                        value,
                        defaultValues.columnOrder,
                        defaultValues.columnPinning
                    );
                    if (encodedValue === null) {
                        return acc;
                    }
                    return {
                        ...acc,
                        [COLUMN_PINNING_PARAM_NAME]: encodedValue,
                    };
                }
                default:
                    return assertNever(key);
            }
        },
        {}
    );

    const searchState = {
        ...existingParams,
        ...searchParams,
    };

    // Stamp the baseline fingerprint alongside any positional param so the
    // reader can detect a later default-order change and reset. When no
    // positional param remains (e.g. the layout was reset to defaults), drop
    // the fingerprint too so no dangling param is left behind. Decided from the
    // final params — positional params carried over from the incoming URL count.
    const params = withBaselineFingerprint(
        new URLSearchParams(searchState),
        columnBaselineFingerprint(defaultValues.columnOrder)
    );

    const dataDefaults = defaultValues.dataParams ?? {};
    Object.entries(dataParamsCodecMap).forEach(([key, codec]) => {
        const stored =
            key in storedDataParams ? storedDataParams[key] : dataDefaults[key];
        const value = coerceDataParam(codec, stored, dataDefaults[key]);
        const encoded = encodeDataParam(codec, value, dataDefaults[key]);
        if (encoded === null) {
            params.delete(key);
        } else {
            params.set(key, encoded);
        }
    });

    params.sort();
    newURL.search = params.toString();

    return newURL.pathname + newURL.search + newURL.hash;
}
